import React, { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { PlusIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext.tsx';
import companiesApi from '../services/companies.ts';
import { formatCurrency } from '../utils/currency.ts';
import { formatStatus } from '../utils/statusFormatter.ts';
import { formatCnpj, stripCnpj } from '../utils/cnpj.ts';
import PageHeader from '../components/ui/PageHeader.tsx';
import StatusChip from '../components/ui/StatusChip.tsx';
import ConfirmationModal from '../components/ConfirmationModal.tsx';
import StackCell from '../components/ui/StackCell.tsx';
import OpenAction from '../components/ui/OpenAction.tsx';

const Companies: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { hasPermission } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState<string | number | null>(null);
  const [deleting, setDeleting] = useState(false);

  const { data, isLoading, error } = useQuery('companies', () => companiesApi.list());

  const filtered = useMemo(() => {
    return (data || []).filter((item: any) => {
      const q = searchTerm.toLowerCase();
      const matchesSearch =
        !q ||
        String(item.name || '').toLowerCase().includes(q) ||
        formatCnpj(item.cnpj).toLowerCase().includes(q) ||
        stripCnpj(item.cnpj || '').includes(q.replace(/\D/g, ''));
      const matchesStatus = statusFilter === 'all' || item.status === statusFilter;
      return matchesSearch && matchesStatus;
    });
  }, [data, searchTerm, statusFilter]);

  const valuation = filtered.reduce((s: number, i: any) => s + Number(i.valuation || 0), 0);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500" />
      </div>
    );
  }
  if (error) return <div className="field-error">Erro ao carregar empresas.</div>;

  return (
    <div>
      <PageHeader
        breadcrumb="Patrimônio / Empresas"
        title="Empresas"
        subtitle="Empresas do patrimônio"
        actions={
          hasPermission('companies.create') || hasPermission('assets.create') ? (
            <button type="button" className="btn btn-primary" onClick={() => navigate('/companies/novo')}>
              <PlusIcon style={{ width: 18, height: 18 }} />
              Novo
            </button>
          ) : undefined
        }
      />

      <div className="toolbar">
        <div className="filters">
          <label className="filter-field">
            <span className="filter-label">Status</span>
            <select className="select" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
              <option value="all">Todos</option>
              <option value="active">Ativa</option>
              <option value="inactive">Inativa</option>
            </select>
          </label>
          <label className="filter-field" style={{ flex: 1 }}>
            <span className="filter-label">Buscar</span>
            <input
              className="input search"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              placeholder="Nome ou CNPJ"
            />
          </label>
        </div>
      </div>

      <div className="kpi-row" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
        <div className="kpi">
          <div className="kpi-label">Empresas</div>
          <div className="kpi-value">{filtered.length}</div>
        </div>
        <div className="kpi">
          <div className="kpi-label">Valuation</div>
          <div className="kpi-value">{formatCurrency(valuation)}</div>
        </div>
        <div className="kpi">
          <div className="kpi-label">Ativas</div>
          <div className="kpi-value">
            {(data || []).filter((i: any) => i.status === 'active').length}
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <div className="card-title">Empresas</div>
          <span className="muted">{filtered.length} registros</span>
        </div>
        {filtered.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center' }} className="muted">
            Nenhuma empresa encontrada.
          </div>
        ) : (
          <div className="table-wrap">
            <table className="data-table compact-mobile">
              <thead>
                <tr>
                  <th>Empresa</th>
                  <th className="hide-m">CNPJ</th>
                  <th className="hide-m">Status</th>
                  <th className="hide-m col-valuation" style={{ textAlign: 'right' }}>
                    Valuation
                  </th>
                  <th style={{ textAlign: 'right' }}>Ações</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((item: any) => (
                  <tr key={item.id}>
                    <td>
                      <StackCell
                        title={item.name}
                        meta={item.cnpj ? formatCnpj(item.cnpj) : undefined}
                        stackExtra={
                          <>
                            {formatStatus(item.status || 'active')} · {formatCurrency(Number(item.valuation || 0))}
                          </>
                        }
                      />
                    </td>
                    <td className="mono hide-m">{formatCnpj(item.cnpj)}</td>
                    <td className="hide-m">
                      <StatusChip
                        label={formatStatus(item.status || 'active')}
                        tone={item.status === 'inactive' ? 'slate' : 'green'}
                      />
                    </td>
                    <td className="amount hide-m" style={{ textAlign: 'right' }}>
                      {formatCurrency(Number(item.valuation || 0))}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <div className="row-actions">
                        <OpenAction onClick={() => navigate(`/companies/${item.id}`)} />
                        {hasPermission('companies.delete') && (
                          <button
                            type="button"
                            className="link-btn action-keep action-danger"
                            onClick={() => { setDeleteId(item.id); setShowDeleteModal(true); }}
                          >
                            Excluir
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => { setShowDeleteModal(false); setDeleteId(null); }}
        title="Excluir Empresa"
        message="Tem certeza que deseja excluir esta empresa? Esta ação não pode ser desfeita."
        onConfirm={async () => {
          if (deleteId == null) return;
          setDeleting(true);
          try {
            await companiesApi.remove(Number(deleteId));
            queryClient.invalidateQueries(['companies']);
          } finally {
            setDeleting(false);
            setShowDeleteModal(false);
            setDeleteId(null);
          }
        }}
        confirmText="Excluir"
        cancelText="Cancelar"
        type="danger"
        isLoading={deleting}
      />
    </div>
  );
};

export default Companies;
